import React, { useCallback, useEffect } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
	isMobileBrowser,
	supportedWallet,
	WALLETS,
} from 'data/supportedWallet';
import { useAccount, useConnect, useSignMessage } from 'wagmi';
import { toast } from 'react-toastify';
import { networkConfig } from 'config';
import { authActions } from 'store/auth';
import { getErrorMessage } from 'data/errors';
import { getAuthNonce } from 'apis';
import { copyToClipboard } from 'utils/copyToClipboard';
import { AUTH_TOKEN } from 'data/constant';

const Row = styled('div')`
	display: flex;
	flex-direction: row;
	margin-bottom: 25px;
	align-items: center;

	:hover {
		cursor: pointer;
	}
`;

const Available = styled(Typography)`
	background-color: #4bb543;
	padding: 4px 7px;
	border-radius: 4px;
	color: #fff;
	font-weight: 500;
	font-size: 12px;
`;

const style = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	width: 350,
	backgroundColor: 'white',
	boxShadow: 18,
	clipPath:
		'polygon(18px 0, calc(100% - 18px) 0, 100% 18px, 100% calc(100% - 18px), calc(100% - 18px) 100%, 18px 100%, 0 calc(100% - 18px), 0 18px)',
	p: 4,
	outline: 'none',
	minHeight: 300,
};

const titleBox = {
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
};

const containerStyle = {
	display: 'flex',
	justifyContent: 'space-between',
};

const WalletConnectModal: React.ComponentType<{}> = () => {
	const dispatch = useDispatch();
	const { connect, connectors } = useConnect();
	const { wallets } = useSelector((state: RootState) => state.vault);
	const { selectedNetwork, isWalletOpen } = useSelector(
		(state: RootState) => state.auth,
	);
	const { terms } = useSelector((state: RootState) => state.auth);
	const { signMessageAsync } = useSignMessage();
	const isWalletInstalled = useCallback(
		(connectorId: string): boolean => {
			const connector = connectors.find((c) => c.id === connectorId);
			return !!connector;
		},
		[connectors],
	);
	const { address } = useAccount();

	const installedWallet = wallets
		.filter((d) =>
			isMobileBrowser() ? d.endsWith('Mobile') : !d.endsWith('Mobile'),
		)
		.filter((d) => isWalletInstalled(d));
	const availableWallet = wallets
		.filter((d) =>
			isMobileBrowser() ? d.endsWith('Mobile') : !d.endsWith('Mobile'),
		)
		.filter((d) => !isWalletInstalled(d));

	const onClose = useCallback(() => {
		dispatch(authActions.openWallet(false));
	}, [dispatch]);

	const onConnect = useCallback(async () => {
		try {
			const res = await getAuthNonce(address || '');
			dispatch(authActions.setAuthMessage(res));
			/*			const signature = await signMessageAsync({
				message: res.authMessage,
			});*/

			/*const authRes = await authSignUpLogin({
				message: res.authMessage,
				signature,
			});*/

			localStorage.setItem('token', AUTH_TOKEN);
			// localStorage.setItem('token', authRes.token);
			//localStorage.setItem('appTId', authRes.id);
			/*dispatch(
				authActions.setLogin({

					wallet: authRes.wallet,
					token: authRes.token,
				}),
			);*/
		} catch (e) {
			//
		}
	}, [address, dispatch]);

	useEffect(() => {
		const token = localStorage.getItem('token');

		if (address && token === null) {
			onConnect();
		} else {
			//
		}
	}, [address, onConnect]);

	const onWalletClick = useCallback(
		async (id: string, getWallet: string) => {
			const text = window.location.href;
			const mobileWallet = Object.values(supportedWallet).find(
				(idx) => idx.id === id,
			);

			const selectedConnector = connectors.find((f) => f.id === id);

			if (
				mobileWallet?.openWallet &&
				isMobileBrowser() &&
				!selectedConnector
			) {
				mobileWallet?.openWallet();
				copyToClipboard(text);
				return;
			}

			if (selectedConnector) {
				connect(
					{
						connector: selectedConnector,
						chainId: networkConfig[selectedNetwork]
							.chainId as number,
					},
					{
						onSuccess: () => {
							onClose?.();
							if (!terms.isAccepted) {
								dispatch(
									authActions.setAccepted({
										open: true,
										value: false,
									}),
								);
							}
						},
						onError: (e: any) => {
							toast(
								getErrorMessage(e?.code, e) ||
									e.details ||
									'Something went wrong',
								{
									type: 'error',
								},
							);
						},
					},
				);
			} else {
				window.open(getWallet, '_blank');
			}
		},
		[
			connect,
			connectors,
			dispatch,
			onClose,
			selectedNetwork,
			terms.isAccepted,
		],
	);

	const RenderItem = useCallback(
		({ d }: { d: WALLETS }) => {
			const { icon, name, id, getWallet } = supportedWallet[d];
			const installed = isWalletInstalled(id);

			return (
				<Row onClick={() => onWalletClick(id, getWallet)}>
					<img width={35} height={35} alt={d} src={icon} />
					<Box width={14} />
					<Typography
						fontWeight={'bold'}
						display={'inline'}
						variant={'body2'}
					>
						{name} <br />
						{installed
							? ''
							: !isMobileBrowser() && (
									<Available display={'inline'}>
										Not Installed
									</Available>
							  )}
					</Typography>
				</Row>
			);
		},
		[isWalletInstalled, onWalletClick],
	);

	return (
		<Modal
			open={isWalletOpen}
			onClose={onClose}
			aria-labelledby="custom-modal-title"
			aria-describedby="custom-modal-description"
			disableAutoFocus
			disableEnforceFocus
			style={{ zIndex: 4100 }}
		>
			<Box sx={style}>
				<Box sx={containerStyle}>
					<Box sx={titleBox}>
						<Typography
							id="custom-modal-title"
							variant="subtitle2"
							fontWeight={600}
							alignSelf={'center'}
							color={'#345CF8'}
						>
							Choose your preferred wallet
						</Typography>
						<Box height={26} />
						<>
							{installedWallet.map((d) => (
								<RenderItem key={d} d={d} />
							))}

							{availableWallet.map((d) => (
								<RenderItem key={d} d={d} />
							))}
						</>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
};

export default WalletConnectModal;
